import { useState } from "react";
import api from "@/lib/api";
import { useAuth, isLocationUser, homeLocationOf } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { formatApiError, todayISO, LOCATIONS } from "@/lib/utils";
import { PackagePlus, CheckCircle2, Plus, X } from "lucide-react";

export default function Inward() {
  const { user } = useAuth();
  const home = homeLocationOf(user);
  const locked = isLocationUser(user);

  const blank = {
    batch_number: "",
    production_date: todayISO(),
    shift: "day",
    bags_count: "",
    operator: "",
    notes: "",
    supplier_company: "",
    location: home || "FACTORY",
  };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");
  const [dupPrompt, setDupPrompt] = useState(null); // existing-batch info on 409

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildPayload = (mergeIfExists = false) => ({
    ...form,
    bags_count: parseInt(form.bags_count, 10),
    merge_if_exists: mergeIfExists,
  });

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setSuccess(null); setLoading(true);
    try {
      const res = await api.post("/batches", buildPayload(false));
      setSuccess({ ...res.data, _merged: false });
      setForm(blank);
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_batch_number") {
        setDupPrompt(detail);
      } else {
        setErr(formatApiError(e2));
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmMerge = async () => {
    setLoading(true); setErr("");
    try {
      const res = await api.post("/batches", buildPayload(true));
      setSuccess({ ...res.data, _merged: true, _added: parseInt(form.bags_count, 10) });
      setForm(blank);
      setDupPrompt(null);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-3xl">
      <PageHeader
        overline="STORE MANAGER"
        title="Finished Goods Inward Entry"
        description="Record production output entering the warehouse. Each bag = 50 kg. Enter a unique batch number — if the same number already exists you'll be prompted to add bags to it."
      />

      {success && (
        <div className="card p-4 mb-6 flex items-center gap-3" style={{ borderColor: "var(--green)" }} data-testid="inward-success">
          <CheckCircle2 size={20} color="var(--green)" />
          <div>
            <div className="font-medium">
              {success._merged
                ? `Added ${success._added} bag(s) to existing batch ${success.batch_number}`
                : "Batch recorded"}
            </div>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
              {success.batch_number} · now {success.bags_count} bags @ {success.location} · status {success.status}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="card p-6 space-y-5" data-testid="inward-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label>Batch Number</label>
            <input
              value={form.batch_number}
              onChange={set("batch_number")}
              required
              placeholder="Type your batch number"
              data-testid="batch-number-input"
            />
          </div>
          <div>
            <label>{form.location === "FACTORY" ? "Production Date" : "Arrival Date"}</label>
            <input type="date" value={form.production_date} onChange={set("production_date")} required data-testid="production-date-input" />
          </div>
          <div>
            <label>Shift</label>
            <select value={form.shift} onChange={set("shift")} data-testid="shift-select">
              <option value="day">Day Shift</option>
              <option value="night">Night Shift</option>
            </select>
          </div>
          <div>
            <label>Number of Bags (50 kg each)</label>
            <input type="number" min="1" value={form.bags_count} onChange={set("bags_count")} required placeholder="e.g. 120" data-testid="bags-count-input" />
          </div>
          <div>
            <label>Operator / Foreman</label>
            <input value={form.operator} onChange={set("operator")} placeholder="Optional" data-testid="operator-input" />
          </div>
          {form.location !== "FACTORY" && (
            <div>
              <label>Supplier / Company</label>
              <input
                value={form.supplier_company}
                onChange={set("supplier_company")}
                placeholder="e.g. Coastal Feeds Ltd"
                data-testid="supplier-company-input"
              />
            </div>
          )}
          <div>
            <label>Location (Storage){locked && " — locked to your warehouse"}</label>
            <select value={form.location} onChange={set("location")} disabled={locked} data-testid="location-select">
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label>Notes</label>
          <textarea rows="3" value={form.notes} onChange={set("notes")} placeholder="Optional remarks..." data-testid="notes-input" />
        </div>

        {form.bags_count && (
          <div className="text-xs font-mono p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>
            → TOTAL WEIGHT: <span style={{ color: "var(--orange)" }}>{(parseInt(form.bags_count, 10) * 50).toLocaleString()} KG</span> · STATUS ON SAVE: PENDING_QC · LOCATION: {form.location}
          </div>
        )}

        {err && (
          <div className="text-xs p-3 border" style={{ color: "var(--red)", borderColor: "var(--red)", background: "rgba(239,68,68,0.1)" }} data-testid="inward-error">
            {err}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" className="btn btn-primary" disabled={loading} data-testid="submit-inward-button">
            <PackagePlus size={16} /> {loading ? "Saving..." : "Record Inward"}
          </button>
        </div>
      </form>

      {/* Duplicate batch number prompt */}
      {dupPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setDupPrompt(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="duplicate-batch-modal">
            <div className="flex items-start justify-between mb-3">
              <div className="text-xs font-mono" style={{ color: "var(--amber)" }}>// DUPLICATE BATCH NUMBER</div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setDupPrompt(null)}><X size={16} /></button>
            </div>
            <h3 className="font-heading text-xl font-bold mb-3">Batch already exists</h3>
            <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              You are entering this batch number a second time. The existing batch is:
            </div>
            <div className="p-3 mb-4 font-mono text-xs space-y-1" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
              <div>Batch: <span style={{ color: "var(--orange)" }}>{form.batch_number}</span></div>
              <div>Current bags: <span style={{ color: "var(--text)" }}>{dupPrompt.existing_bags_count}</span> (remaining {dupPrompt.existing_bags_remaining})</div>
              <div>Status: <span style={{ color: "var(--text)" }}>{dupPrompt.existing_status}</span></div>
              <div>Location: <span style={{ color: "var(--text)" }}>{dupPrompt.existing_location}</span></div>
            </div>
            <div className="text-sm mb-4">
              Do you want to add <span style={{ color: "var(--orange)" }} className="font-mono">{form.bags_count}</span> more bag(s) to this existing batch?
            </div>
            {dupPrompt.existing_location !== form.location && (
              <div className="text-xs p-2 mb-4" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid var(--red)", color: "var(--red)" }}>
                ⚠ Location mismatch — existing is at {dupPrompt.existing_location}, you selected {form.location}. Merge will be refused. Change the location or use a different batch number.
              </div>
            )}
            <div className="flex gap-3">
              <button className="btn btn-secondary flex-1" onClick={() => setDupPrompt(null)} data-testid="cancel-merge-button">Cancel</button>
              <button
                className="btn btn-primary flex-1"
                onClick={confirmMerge}
                disabled={loading || dupPrompt.existing_location !== form.location}
                data-testid="confirm-merge-button"
              >
                <Plus size={14} /> Add to Existing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
